/**
 * Hook for managing Supercluster instance and operations
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type Supercluster from 'supercluster';
import { GeoJsonProperties } from 'geojson';

import { superclusterOptions } from '../constants';
import type { GeoJSONFeature, SuperclusterInstance } from '../utils/types';

export const useSupercluster = (options?: Supercluster.Options<GeoJsonProperties, Record<string, unknown>>) => {
  const [supercluster, setSupercluster] = useState<SuperclusterInstance | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [featuresLoaded, setFeaturesLoaded] = useState(false);
  const featuresLoadTimeRef = useRef<number>(0);
  const optionsRef = useRef(options || superclusterOptions);

  // Update options ref when options change
  useEffect(() => {
    optionsRef.current = options || superclusterOptions;
  }, [options]);

  useEffect(() => {
    const loadSupercluster = async () => {
      try {
        const Supercluster = (await import('supercluster')).default;
        const cluster = new Supercluster(optionsRef.current) as SuperclusterInstance;
        setSupercluster(cluster);
        setIsLoaded(true);
        // Reset features loaded when options change
        setFeaturesLoaded(false);
      } catch (error) {
        console.error('Failed to load supercluster:', error);
      }
    };

    loadSupercluster();
  }, [options]);

  const loadFeatures = useCallback(
    (features: GeoJSONFeature[]) => {
      if (!supercluster) {
        setFeaturesLoaded(false);
        return;
      }

      // Validate features array
      if (!Array.isArray(features)) {
        console.warn('loadFeatures: Invalid features array (not an array):', features);
        setFeaturesLoaded(false);
        return;
      }

      // Allow empty arrays - supercluster can handle them
      try {
        // Validate that features have the required GeoJSON structure
        const validFeatures = features.filter((feature) => {
          if (!feature || typeof feature !== 'object') return false;
          if (!feature.geometry || typeof feature.geometry !== 'object') return false;
          if (!Array.isArray(feature.geometry.coordinates)) return false;
          if (feature.geometry.coordinates.length < 2) return false;
          const [lng, lat] = feature.geometry.coordinates;
          // Validate coordinates are valid numbers within reasonable bounds
          if (
            typeof lng !== 'number' ||
            typeof lat !== 'number' ||
            isNaN(lng) ||
            isNaN(lat) ||
            !isFinite(lng) ||
            !isFinite(lat) ||
            lng < -180 ||
            lng > 180 ||
            lat < -90 ||
            lat > 90
          ) {
            return false;
          }
          return true;
        });

        // Load features into supercluster
        // Only mark as loaded if we have valid features - empty arrays might not build trees
        if (validFeatures.length > 0) {
          supercluster.load(validFeatures);
          featuresLoadTimeRef.current = Date.now();
          setFeaturesLoaded(true);
        } else {
          // If no valid features, ensure supercluster is in a clean state
          supercluster.load([]);
          featuresLoadTimeRef.current = 0;
          setFeaturesLoaded(false);
        }
      } catch (error) {
        console.error('Error loading features into supercluster:', error);
        setFeaturesLoaded(false);
      }
    },
    [supercluster]
  );

  const getClusters = useCallback(
    (bounds: [number, number, number, number], zoom: number) => {
      if (!supercluster || !featuresLoaded) return [];

      // Enhanced bounds validation
      if (!bounds || !Array.isArray(bounds) || bounds.length !== 4) {
        console.warn('Invalid bounds provided to getClusters:', bounds);
        return [];
      }

      if (bounds.some((v) => typeof v !== 'number' || isNaN(v))) {
        console.warn('Bounds contains invalid numbers:', bounds);
        return [];
      }

      if (typeof zoom !== 'number' || isNaN(zoom) || !isFinite(zoom)) {
        console.warn('Invalid zoom provided to getClusters:', zoom);
        return [];
      }

      // Validate zoom is within supercluster's configured range
      const currentOptions = optionsRef.current;
      const minZoom = currentOptions.minZoom ?? 0;
      const maxZoom = currentOptions.maxZoom ?? 20;
      if (zoom < minZoom || zoom > maxZoom) {
        console.warn(`Zoom ${zoom} is outside configured range [${minZoom}, ${maxZoom}]`);
        return [];
      }

      try {
        // Additional check: ensure supercluster has been loaded with features
        // The internal tree structure must be initialized before calling getClusters
        if (!supercluster || typeof supercluster.getClusters !== 'function') {
          console.warn('Supercluster instance is not ready for getClusters');
          return [];
        }

        // Ensure features were loaded recently (at least 10ms ago to allow tree building)
        // This prevents calling getClusters immediately after load, before trees are built
        const timeSinceLoad = Date.now() - featuresLoadTimeRef.current;
        if (timeSinceLoad < 10) {
          // Too soon after loading, return empty to avoid accessing uninitialized trees
          return [];
        }

        // Round zoom to integer as supercluster expects integer zoom levels
        // This ensures we're accessing the correct tree level
        const roundedZoom = Math.floor(zoom);

        // Verify zoom is still within range after rounding
        const currentOptions = optionsRef.current;
        const minZoom = currentOptions.minZoom ?? 0;
        const maxZoom = currentOptions.maxZoom ?? 20;
        if (roundedZoom < minZoom || roundedZoom > maxZoom) {
          console.warn(`Rounded zoom ${roundedZoom} is outside configured range [${minZoom}, ${maxZoom}]`);
          return [];
        }

        // Call getClusters with rounded zoom level
        // Supercluster builds trees lazily per zoom level, so this ensures we access the correct tree
        const result = supercluster.getClusters(bounds, roundedZoom);

        // Validate result is an array (supercluster should always return an array)
        if (!Array.isArray(result)) {
          console.warn('Supercluster.getClusters returned non-array result:', result);
          return [];
        }

        return result;
      } catch (error) {
        console.error('Error calling supercluster.getClusters:', error, { bounds, zoom, featuresLoaded });
        return [];
      }
    },
    [supercluster, featuresLoaded]
  );

  const getLeaves = useCallback(
    (clusterId: number, limit?: number) => {
      if (!supercluster) return [];

      try {
        return supercluster.getLeaves(clusterId, limit);
      } catch (error) {
        console.warn(`Failed to get leaves for cluster ${clusterId}:`, error);
        return [];
      }
    },
    [supercluster]
  );

  const getClusterExpansionZoom = useCallback(
    (clusterId: number) => {
      if (!supercluster) return 0;

      try {
        return supercluster.getClusterExpansionZoom(clusterId);
      } catch (error) {
        console.warn(`Failed to get expansion zoom for cluster ${clusterId}:`, error);
        return 0;
      }
    },
    [supercluster]
  );

  return {
    isSuperclusterLoaded: isLoaded,
    featuresLoaded,
    loadFeatures,
    getClusters,
    getLeaves,
    getClusterExpansionZoom
  };
};
